import { jsx, jsxs } from 'react/jsx-runtime';
import { Root as Tabs$1, List as TabsList$1, TabsTrigger as TabsTrigger$1, Content as TabsContent$1 } from '../../vendor/@radix-ui/react-tabs/dist/index.mjs';
import * as React from 'react';
import { useDevice } from '../../contexts/DeviceContext.js';
import { cn } from '../../lib/utils.js';

const Tabs = React.forwardRef(({ activationMode = "manual", ...props }, ref) => /* @__PURE__ */ jsx(Tabs$1, { ref, activationMode, ...props }));
Tabs.displayName = Tabs$1.displayName;
const TabContext = React.createContext({ scroll: false });
const TabsList = React.forwardRef(({ className, variant = "round", scroll = true, offset = 16, ...props }, ref) => {
  const innerRef = React.useRef(null);
  const setRefs = (node) => {
    innerRef.current = node;
    if (typeof ref === "function") {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  };
  React.useEffect(() => {
    const listEl = innerRef.current;
    if (!listEl) return;
    const hasTargetParent = listEl.closest(".content-gray-inner , .fund-layout , .my-notifications");
    if (!hasTargetParent) return;
    const observer = new MutationObserver(() => {
      const activeTrigger = listEl.querySelector('[data-state="active"]');
      if (!activeTrigger) return;
      listEl.scrollTo({
        left: activeTrigger.offsetLeft - offset,
        behavior: "smooth"
      });
    });
    observer.observe(listEl, {
      attributes: true,
      subtree: true,
      attributeFilter: ["data-state"]
    });
    return () => observer.disconnect();
  }, [offset]);
  const { isDesktop } = useDevice();
  const variantStyles = {
    underline: "w-full gap-6",
    round: `w-full gap-2 ${scroll && !isDesktop ? "overflow-x-auto whitespace-nowrap scrollbar-hide" : "overflow-x-visible whitespace-wrap flex-wrap"}`,
    segment: "w-full items-stretch inline-flex bg-basic-background-04 rounded-md flex-none segment"
  };
  return /* @__PURE__ */ jsx(TabContext.Provider, { value: { scroll }, children: /* @__PURE__ */ jsx(
    TabsList$1,
    {
      ref: variant === "round" ? setRefs : ref,
      className: cn(
        "round-tab inline-flex items-center justify-start relative whitespace-nowrap w-[100%] overflow-x-auto overflow-y-hidden",
        variantStyles[variant],
        className
      ),
      ...props
    }
  ) });
});
TabsList.displayName = TabsList$1.displayName;
const TabsTrigger = React.forwardRef(({ className, variant = "round", isNotification, children, ...props }, ref) => {
  const { isDesktop } = useDevice();
  const variantStyles = {
    round: cn(
      "rounded-full h-8 border border-basic-05 bg-base-white body-xsm leading-6 font-weight-medium text-basic-04 pt-[7px] pb-[6px] px-5 data-[state=active]:bg-interactive-active-selected-04 data-[state=active]:text-interactive-active-selected-01 data-[state=active]:border data-[state=active]:border-input data-[state=active]:text-interactive-active-selected-02 data-[state=active]:border-interactive-active-selected-02",
      isDesktop && "pt-[8px] h-[40px] last:mr-0"
    ),
    underline: cn(
      "relative flex-1 pb-3 text-basic-04 body-md font-weight-medium data-[state=active]:text-basic-01 before:absolute before:bottom-0 before:w-full before:h-[2px] data-[state=active]:before:bg-base-black"
    ),
    segment: cn(
      "flex-1 p-2 pb-[6px] min-h-[44px] body-md shadow-4 round-md data-[state=active]:font-weight-medium data-[state=active]:rounded-md data-[state=active]:shadow-[0px_2px_4px_0px_rgba(0,0,0,0.08)] data-[state=active]:bg-primary-background-02 data-[state=active]:text-interactive-inverse text-basic-04",
      isDesktop && "h-[52px] !body-lg"
    )
  };
  return /* @__PURE__ */ jsxs(
    TabsTrigger$1,
    {
      ref,
      className: cn(
        "inline-flex items-center justify-center whitespace-nowrap transition-all disabled:pointer-events-none disabled:opacity-50",
        "focus-visible:!outline-offset-[-2px] focus-visible:!outline-[var(--color-text-basic-01)] focus-visible:!outline-2",
        variantStyles[variant],
        className
      ),
      ...props,
      children: [
        children,
        variant === "underline" && isNotification && /* @__PURE__ */ jsx("span", { className: "block absolute top-0 w-[6px] h-[6px] rounded-full bg-icon-interactive-danger translate-x-[calc(-50%+40px)]", children: /* @__PURE__ */ jsx("span", { className: "sr-only", children: "New" }) })
      ]
    }
  );
});
TabsTrigger.displayName = TabsTrigger$1.displayName;
const TabsContent = React.forwardRef(({ className, variant, ...props }, ref) => {
  return /* @__PURE__ */ jsx(
    TabsContent$1,
    {
      ref,
      tabIndex: -1,
      className: cn("mt-5 ring-offset-background tabs-content", variant, className),
      ...props
    }
  );
});
TabsContent.displayName = TabsContent$1.displayName;

export { Tabs, TabsContent, TabsList, TabsTrigger };
